<template>
  <div
    class="dui-datepicker"
    :class="{ 'always-open': alwaysOpen }"
    :disabled="disabled"
  >
    <DuiIconCalendar
      v-if="!alwaysOpen"
      class="icon-lg calendar-icon"
    />
    <Datepicker
      v-model="vm.value"
      :clear-button="clearButton"
      :clear-button-icon="clearButtonIcon"
      :disabled="disabled"
      :disabled-dates="disabledDates"
      :format="displayFormatter"
      :inline="alwaysOpen"
      :name="name"
      :open-date="openDate"
      :placeholder="placeholder"
      :maximum-view="maxView"
      :minimum-view="minView"
      @input="valueUpdated"
    />
    <DuiIconDown
      v-if="!alwaysOpen"
      class="down-icon"
    />
  </div>
</template>

<script>
import moment from 'moment';
import Datepicker from 'vuejs-datepicker';

import storeKey from '@dui/mixins/store-key';

export default {
  components: {
    Datepicker,
  },
  mixins: [
    storeKey,
  ],
  props: {
    alwaysOpen: {
      type: Boolean,
      default: false,
    },
    clearButton: {
      type: Boolean,
      default: true,
    },
    clearButtonIcon: {
      type: String,
      default: 'button button-icon button-sm button-close',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    disabledDates: {
      type: Object,
      default: () => ({
        to: moment().add(-1, 'days').toDate(),
      }),
    },
    displayFormat: {
      type: String,
      default: 'DD/MM/YYYY',
    },
    format: {
      type: String,
      default: 'YYYY-MM-DD',
    },
    maxView: {
      type: String,
      default: 'year',
    },
    minView: {
      type: String,
      default: 'day',
    },
    openDate: {
      type: Date,
      default: null,
    },
    placeholder: {
      type: String,
      default: 'Choose a date',
    },
    name: {
      type: String,
      required: true,
    },
    value: {
      type: String,
      default: null,
    },
  },
  data: () => ({
    vm: {
      value: null,
    },
  }),
  computed: {
    storeData() {
      return this.loadFromStore();
    },
  },
  watch: {
    value: function () {
      this.setDateFromValue();
    },
  },
  mounted() {
    this.setDateFromValue();
  },
  methods: {
    displayFormatter(date) {
      return date ? moment(date).format(this.displayFormat) : null;
    },

    getDateInFormat(date) {
      return date ? moment(date).format(this.format) : null;
    },

    getDateFromFormat(date) {
      return date ? moment(date, this.format).toDate() : null;
    },

    setDateFromValue() {
      const dateValue = this.storeLoadKey ? this.storeData : this.value;
      this.vm.value = this.getDateFromFormat(dateValue);
    },

    valueUpdated(newVal) {
      const newDate = newVal ? this.getDateInFormat(newVal) : null;
      this.$emit('input', newDate);
      this.$emit('change');

      if (!this.storeUpdateKey) {
        return;
      }

      this.$store.dispatch(this.storeUpdateKey, newDate);
    },
  },
};
</script>

<style lang="scss">
@import "@dui-styles/base";

.dui-datepicker {
  position: relative;
  width: 100%;

  &[disabled] {
    opacity: $disabled-opacity;
    pointer-events: none;
  }

  .calendar-icon,
  .down-icon {
    pointer-events: none;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 1;
  }

  .calendar-icon {
    left: $_2em;
  }

  .down-icon {
    right: $_2em;
  }

  // These styles override the default from
  // https://github.com/charliekassel/vuejs-datepicker
  .vdp-datepicker {
    position: relative;
    width: 100%;

    input {
      padding-left: $_6em;
      padding-right: $_6em;
      width: 100%;

      &:hover {
        cursor: pointer;
      }
    }

    .vdp-datepicker__calendar {
      background-color: getColor($grey);
      border: $border-width-base solid getColor($black);
      border-radius: $border-radius-base;
      box-shadow: $box-shadow-context-menu;
      font-family: $base-font;
      max-width: 327px;
      min-width: 300px;
      top: calc(100% + #{ $_1em });
      width: 100%;
      z-index: $z-index-form-elements;

      header {
        background-color: getColor($black);
        border-top-left-radius: $border-radius-sm;
        border-top-right-radius: $border-radius-sm;
        display: flex;
        color: getColor($white);
        font-weight: $base-weight-bold;
        overflow: hidden;

        .prev:not(.disabled):hover,
        .next:not(.disabled):hover,
        .up:not(.disabled):hover {
          background-color: getColor($black, fade) !important;
        }

        .prev,
        .next,
        .up {
          @include transition((
            background-color,
            opacity
          ));

          &.disabled {
            opacity: $disabled-opacity;
            pointer-events: none;
          }
        }

        .prev,
        .next {
          &:after {
            background-repeat: no-repeat;
            border: none !important;
            content: " ";
            height: $_2em;
            left: 50%;
            margin: 0;
            top: 50%;
            transform: translate3d(-50%, -50%, 0);
            width: $_2em;
          }
        }

        .prev:after {
          background-image: url('../../../assets/icons/icon-left-white.svg');
        }

        .next:after {
          background-image: url('../../../assets/icons/icon-right-white.svg');
        }
      }

      // Days container
      > div {
        border-radius: $border-radius-base;
        overflow: hidden;
      }

      .cell.day-header {
        font-size: $font-size-xs;
        font-weight: $base-weight-bold;
        text-transform: uppercase;
        background: getColor($white);
        &:hover {
          background: getColor($white);
        }
      }

      .cell.day,
      .cell.month,
      .cell.year {
        border: 1px solid getColor($grey);
        background: getColor($white);
        color: getColor($black);
        padding: 0 $_0-5em;

        @include transition((
          background-color
        ));

        &.disabled {
          opacity: $disabled-opacity;
          background-color: getColor($grey);
        }

        &.blank {
          background: none;
        }

        &:not(.blank):not(.disabled) {
          &:hover {
            border: 1px solid getColor($yellow, fade);
            background-color: getColor($yellow, fade);
          }

          &.selected {
            border: 1px solid getColor($black);
            background-color: getColor($black);
            color: getColor($white);
            font-weight: $base-weight-bold;
          }
        }
      }

      .cell.day.today {
        font-weight: $base-weight-bold;
        border: 1px solid getColor($yellow, fade);
        background-color: getColor($yellow, fade);
      }
    }

    .vdp-datepicker__clear-button {
      position: absolute;
      right: 14px;
      top: 50%;
      transform: translateY(-50%);
      z-index: 2;
    }
  }

  &.always-open {
    display: inline-flex;
    width: auto;

    .vdp-datepicker {
      .vdp-datepicker__calendar {
        box-shadow: none;
      }

      .vdp-datepicker__clear-button {
        display: none;
      }
    }
  }
}
</style>
